import React, { ComponentProps, ReactElement } from 'react';

import PropTypes from 'prop-types';
import { useDropzone, DropzoneOptions } from 'react-dropzone';

import theme, {
  stylePropType,
  StylableComponent,
} from '@adsk/alloy-react-theme';

import { DROPZONE_SIZES, Size, sizePropType } from './consts';
import DefaultActiveDragContent from './layout/DefaultActiveDragContent';
import DefaultContent from './layout/DefaultContent';
import DefaultContentSmall from './layout/DefaultContentSmall';

type DropzoneProps = Omit<StylableComponent<HTMLDivElement>, 'onDrop'> &
  DropzoneOptions & {
    disabled?: boolean;
    size?: Size;
    renderContent?: (open: () => void) => ReactElement;
    renderDragActiveContent?: (
      props: ComponentProps<typeof DefaultActiveDragContent>
    ) => ReactElement;
  };

/**
 * Dropzone
 * A Dropzone is a component that provides an easy way to upload files by dragging
 * and dropping or by selecting the file’s location.
 * It supports multiple file uploads.
 */
const Dropzone = React.forwardRef<HTMLDivElement, DropzoneProps>(
  (
    {
      size = DROPZONE_SIZES.DEFAULT,
      style,
      disabled,
      className,
      renderContent,
      renderDragActiveContent = (dragActiveContentProps) => (
        <DefaultActiveDragContent {...dragActiveContentProps} />
      ),
      ...props
    },
    ref
  ) => {
    const { isDragActive, getRootProps, getInputProps, open } = useDropzone({
      disabled,
      ...props,
    });

    return (
      <div
        ref={ref}
        className={className}
        role="button"
        data-testid="dropzone"
        css={[
          {
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            backgroundColor: theme.colors.white,
            borderRadius: theme.borderRadius.small,
            border: `1px dashed ${theme.colors.charcoal400}`,
            padding: `${theme.spacing.large}px ${theme.spacing.medium}px`,
            overflow: 'hidden',
            cursor: isDragActive ? 'grabbing' : 'pointer',
            transition: theme.transitions.create(
              ['border-color', 'background-color'],
              {
                duration: 150,
              }
            ),
            ...(!disabled &&
              !isDragActive && {
                '&:hover': {
                  borderColor: theme.colors.adskBlue500,
                },
              }),
            ...(disabled && {
              opacity: 0.35,
              cursor: 'not-allowed',
            }),
            ...(size === DROPZONE_SIZES.SMALL && {
              height: 64,
            }),
          },
          style,
        ]}
        {...getRootProps()}
      >
        <input {...getInputProps()} />
        <div
          css={[
            {
              position: 'absolute',
              background: theme.colors.charcoal050,
              opacity: !isDragActive ? 0 : 1,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              transition: theme.transitions.create(['opacity'], {
                duration: 150,
              }),
              zIndex: isDragActive ? 1 : -1,
            },
          ]}
        >
          {isDragActive &&
            renderDragActiveContent &&
            renderDragActiveContent({ size })}
        </div>
        {renderContent && renderContent(open)}
        {!renderContent && size === DROPZONE_SIZES.SMALL && (
          <DefaultContentSmall disabled={disabled} open={open} />
        )}
        {!renderContent && size === DROPZONE_SIZES.DEFAULT && (
          <DefaultContent disabled={disabled} open={open} />
        )}
      </div>
    );
  }
);

Dropzone.displayName = 'Dropzone';

Dropzone.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Whether the dropzone is disabled */
  disabled: PropTypes.bool,
  /** Dropzone size */
  size: sizePropType,
  /** Callback to execute on file drop */
  onDrop: PropTypes.func,
  /** File types to accept */
  accept: PropTypes.string,
  /** Content to render */
  renderContent: PropTypes.func,
  /** Content to render when drag is active */
  renderDragActiveContent: PropTypes.func,
};

export default Object.assign(Dropzone, {
  SIZES: DROPZONE_SIZES,
  Content: DefaultContent,
  ContentSmall: DefaultContentSmall,
});
